"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import {
  Copy,
  MoreHorizontal,
  Pencil,
  Pin,
  PinOff,
  Trash2,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SIDEBAR_NAV_STYLES,
  Switch,
} from "ui";

import { FullPost } from "@/lib/types";
import { useMember } from "@/lib/state/member";
import { trpc } from "@/lib/trpc/client";
import { ChannelSummary } from "@/lib/types/channel";
import CreatePostModal from "@/features/posts/components/create-post-modal";
import { ADMIN_ROUTES } from "@/lib/routes";

/**
 * Pin context for the surface rendering this menu (TRA-1582). When provided,
 * OWNER/ADMIN viewers get a Pin/Unpin action scoped to that surface — HOME
 * for the home feed, CHANNEL for a channel feed. One post per scope; pinning
 * over an existing pin asks for confirmation first.
 */
export interface PostPinContext {
  scope: "HOME" | "CHANNEL";
  /** Id of the post currently pinned in this scope (null = nothing pinned). */
  pinnedPostId: string | null;
  /** Title of the currently pinned post, for the replace-confirm copy. */
  pinnedPostTitle?: string | null;
  /** Called after a successful pin/unpin so the surface can refetch. */
  onPinChanged?: () => void;
}

interface PostMoreMenuProps {
  post: FullPost;
  /** Compact trigger for feed lists — smaller icon, no background on hover. */
  compact?: boolean;
  /** Called after the post is successfully deleted (e.g. to animate removal). */
  onDeleted?: () => void;
  /** Enables the staff Pin/Unpin action for the rendering surface. */
  pin?: PostPinContext;
}

type PostFlagKey = "hideLikes" | "hideComments" | "commentsClosed";

/** Dialogs this menu opens — all of them must wait for the menu to unmount. */
type MenuDialog = "delete" | "replacePin" | "edit";

const POSTS_CONTENT_TYPE = "POSTS";

export default function PostMoreMenu({
  post,
  compact,
  onDeleted,
  pin,
}: PostMoreMenuProps) {
  const router = useRouter();
  const { permissions } = useMember();
  const isAdmin = permissions.isOwnerOrAdmin;
  const utils = trpc.useUtils();
  const duplicatePostMutation = trpc.posts.duplicate.useMutation();
  const deletePostMutation = trpc.posts.delete.useMutation();
  const updateFlagsMutation = trpc.posts.updateFlags.useMutation();
  const followMutation = trpc.posts.follow.useMutation();
  const unfollowMutation = trpc.posts.unfollow.useMutation();
  const pinMutation = trpc.posts.pin.useMutation();
  const unpinMutation = trpc.posts.unpin.useMutation();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // Replace-pin confirmation (TRA-1582). `replaceTitle` is the currently
  // pinned post's title for the dialog copy — from the surface's pin context,
  // or from the server when the context was stale.
  const [pinConfirmOpen, setPinConfirmOpen] = useState(false);
  const [replaceTitle, setReplaceTitle] = useState<string | null>(null);
  const [isPinning, setIsPinning] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [channels, setChannels] = useState<
    { id: string; name: string; emoji?: string | null }[]
  >([]);

  // Local optimistic flag state, seeded from the post
  const [hideLikes, setHideLikes] = useState<boolean>(Boolean(post.hideLikes));
  const [hideComments, setHideComments] = useState<boolean>(
    Boolean(post.hideComments),
  );
  const [commentsClosed, setCommentsClosed] = useState<boolean>(
    Boolean(post.commentsClosed),
  );

  // Controlled so follow status is only fetched while the menu is open.
  const [menuOpen, setMenuOpen] = useState(false);

  // TRA-1970 — never mount one of the dialogs below while the dropdown is
  // still on screen. Radix sets `body { pointer-events: none }` for a modal
  // layer and restores the value it saw when the *first* layer opened. The
  // menu and the dialogs resolve to different copies of
  // @radix-ui/react-dismissable-layer (menu 1.1.11, dialog 1.1.12), so each
  // keeps its own "original" value: a dialog opened from `onSelect` mounts
  // while the menu is still playing its 150ms close animation, records the
  // menu's `none` as the original, and reinstates it when the dialog closes —
  // leaving the entire page unclickable until a reload. Handing over once the
  // menu content has actually unmounted keeps the two layers from overlapping.
  const isMenuContentMountedRef = useRef(false);
  const pendingDialogRef = useRef<MenuDialog | null>(null);

  const openDialog = useCallback((dialog: MenuDialog) => {
    if (dialog === "delete") setConfirmOpen(true);
    if (dialog === "replacePin") setPinConfirmOpen(true);
    if (dialog === "edit") setEditModalOpen(true);
  }, []);

  const requestDialog = useCallback(
    (dialog: MenuDialog) => {
      if (isMenuContentMountedRef.current) {
        pendingDialogRef.current = dialog;
        return;
      }
      openDialog(dialog);
    },
    [openDialog],
  );

  const handleMenuOpenChange = useCallback((open: boolean) => {
    if (open) isMenuContentMountedRef.current = true;
    setMenuOpen(open);
  }, []);

  // Fires when the menu content unmounts (after its close animation), which is
  // the earliest point a dialog can open without inheriting the menu's
  // pointer-events lock.
  const handleMenuCloseAutoFocus = useCallback(
    (event: Event) => {
      isMenuContentMountedRef.current = false;
      const pending = pendingDialogRef.current;
      if (!pending) return;
      pendingDialogRef.current = null;
      // The dialog takes focus itself — don't bounce it back to the trigger.
      event.preventDefault();
      openDialog(pending);
    },
    [openDialog],
  );

  // Follow state — gated on menuOpen so the feed doesn't fire one
  // followState request per post on load (N+1).
  const { data: followData } = trpc.posts.followState.useQuery(
    { postId: post.id },
    { enabled: menuOpen },
  );
  const isFollowing = Boolean(followData?.isFollowing);

  // Fetch channels when edit modal opens
  useEffect(() => {
    if (!editModalOpen || channels.length > 0) return;
    utils.client.navigation.sidebar
      .query()
      .then((data) => {
        const postChannels = (
          (data.channels ?? []) as unknown as ChannelSummary[]
        )
          .filter((ch: ChannelSummary) => ch.contentType === POSTS_CONTENT_TYPE)
          .map((ch: ChannelSummary) => ({
            id: ch.id,
            name: ch.name,
            emoji: ch.emoji,
          }));
        setChannels(postChannels);
      })
      .catch(() => {
        // Non-critical — modal will show "Select channel" placeholder
      });
  }, [editModalOpen, channels.length, utils]);

  const handleEdit = useCallback(() => {
    requestDialog("edit");
  }, [requestDialog]);

  const handleDuplicate = useCallback(async () => {
    try {
      const res = await duplicatePostMutation.mutateAsync({
        postId: post.id,
      });
      toast.success("Post duplicated");
      router.push(ADMIN_ROUTES.postSettings(res.slug));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to duplicate post",
      );
    }
  }, [post.id, router, duplicatePostMutation]);

  const handleRequestDelete = useCallback(() => {
    requestDialog("delete");
  }, [requestDialog]);

  const handleConfirmDelete = useCallback(async () => {
    setIsDeleting(true);
    try {
      await deletePostMutation.mutateAsync({ postId: post.id });
      setIsDeleting(false);
      toast.success("Post deleted");
      setConfirmOpen(false);
      if (onDeleted) {
        onDeleted();
      } else {
        router.push("/");
      }
    } catch (error) {
      setIsDeleting(false);
      toast.error(
        error instanceof Error ? error.message : "Failed to delete post",
      );
    }
  }, [post.id, router, onDeleted, deletePostMutation]);

  const isPinnedHere = !!pin && pin.pinnedPostId === post.id;

  const handlePinChanged = useCallback(() => {
    pin?.onPinChanged?.();
    // Home feed pin state is server-fetched — refresh re-renders the feed.
    router.refresh();
  }, [pin, router]);

  const handleConfirmReplacePin = useCallback(async () => {
    if (!pin) return;
    setIsPinning(true);
    try {
      await pinMutation.mutateAsync({
        postId: post.id,
        scope: pin.scope,
        confirmReplace: true,
      });
      setPinConfirmOpen(false);
      toast.success("Pinned post replaced");
      handlePinChanged();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to pin post",
      );
    } finally {
      setIsPinning(false);
    }
  }, [pin, post.id, pinMutation, handlePinChanged]);

  const handleTogglePin = useCallback(async () => {
    if (!pin) return;
    if (isPinnedHere) {
      try {
        await unpinMutation.mutateAsync({ postId: post.id, scope: pin.scope });
        toast.success("Post unpinned");
        handlePinChanged();
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to unpin post",
        );
      }
      return;
    }

    // Another post is already pinned in this scope — confirm before replacing.
    if (pin.pinnedPostId && pin.pinnedPostId !== post.id) {
      setReplaceTitle(pin.pinnedPostTitle ?? null);
      requestDialog("replacePin");
      return;
    }

    try {
      const res = await pinMutation.mutateAsync({
        postId: post.id,
        scope: pin.scope,
        confirmReplace: false,
      });
      // The surface's pin context was stale and something else is pinned —
      // fall back to the server-provided title for the confirm dialog.
      if (res.status === "requiresConfirmation") {
        setReplaceTitle(res.existing.title ?? null);
        requestDialog("replacePin");
        return;
      }
      toast.success("Post pinned");
      handlePinChanged();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to pin post",
      );
    }
  }, [
    pin,
    isPinnedHere,
    post.id,
    pinMutation,
    unpinMutation,
    handlePinChanged,
    requestDialog,
  ]);

  const handleToggleFollow = useCallback(async () => {
    const next = !isFollowing;
    // Optimistic
    utils.posts.followState.setData({ postId: post.id }, { isFollowing: next });
    try {
      if (next) {
        await followMutation.mutateAsync({ postId: post.id });
      } else {
        await unfollowMutation.mutateAsync({ postId: post.id });
      }
      await utils.posts.followState.invalidate({ postId: post.id });
    } catch (err) {
      // Revert on error
      utils.posts.followState.setData(
        { postId: post.id },
        { isFollowing: !next },
      );
      toast.error("Failed to update follow state");
    }
  }, [post.id, isFollowing, followMutation, unfollowMutation, utils]);

  const handleToggleFlag = useCallback(
    async (flag: PostFlagKey, next: boolean) => {
      // Optimistic local update
      if (flag === "hideLikes") setHideLikes(next);
      if (flag === "hideComments") setHideComments(next);
      if (flag === "commentsClosed") setCommentsClosed(next);

      try {
        await updateFlagsMutation.mutateAsync({
          postId: post.id,
          [flag]: next,
        });
        router.refresh();
      } catch (error) {
        // Revert on error
        if (flag === "hideLikes") setHideLikes(!next);
        if (flag === "hideComments") setHideComments(!next);
        if (flag === "commentsClosed") setCommentsClosed(!next);
        toast.error(
          error instanceof Error ? error.message : "Failed to update post",
        );
      }
    },
    [post.id, router, updateFlagsMutation],
  );

  const handleToggleHideLikes = useCallback(() => {
    void handleToggleFlag("hideLikes", !hideLikes);
  }, [handleToggleFlag, hideLikes]);

  const handleToggleHideComments = useCallback(() => {
    void handleToggleFlag("hideComments", !hideComments);
  }, [handleToggleFlag, hideComments]);

  const handleToggleCommentsClosed = useCallback(() => {
    void handleToggleFlag("commentsClosed", !commentsClosed);
  }, [handleToggleFlag, commentsClosed]);

  const handleToggleFollowItem = useCallback(() => {
    void handleToggleFollow();
  }, [handleToggleFollow]);

  const preventClose = useCallback((event: Event) => {
    event.preventDefault();
  }, []);

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={handleMenuOpenChange}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            aria-label="Post options"
            className={
              compact
                ? "rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
                : "rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            }
          >
            <MoreHorizontal className={compact ? "h-4 w-4" : "h-5 w-5"} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={4}
          className="w-56"
          onCloseAutoFocus={handleMenuCloseAutoFocus}
        >
          {isAdmin && (
            <>
              {pin && !!post.published && (
                <DropdownMenuItem onSelect={handleTogglePin}>
                  {isPinnedHere ? (
                    <PinOff className="h-3.5 w-3.5" />
                  ) : (
                    <Pin className="h-3.5 w-3.5" />
                  )}
                  {pin.scope === "HOME"
                    ? isPinnedHere
                      ? "Unpin from home feed"
                      : "Pin to home feed"
                    : isPinnedHere
                      ? "Unpin from channel"
                      : "Pin to channel"}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={handleEdit}>
                <Pencil className="h-3.5 w-3.5" />
                Edit post
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={handleDuplicate}>
                <Copy className="h-3.5 w-3.5" />
                Duplicate post
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={handleRequestDelete}
                className={SIDEBAR_NAV_STYLES.destructive}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete post
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          <DropdownMenuItem
            onSelect={preventClose}
            onClick={handleToggleFollowItem}
            className="flex items-center justify-between gap-4"
          >
            <span>Follow post</span>
            <Switch
              checked={isFollowing}
              onCheckedChange={handleToggleFollowItem}
              onClick={(e) => e.stopPropagation()}
            />
          </DropdownMenuItem>

          {isAdmin && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={preventClose}
                onClick={handleToggleHideLikes}
                className="flex items-center justify-between gap-4"
              >
                <span>Hide likes</span>
                <Switch
                  checked={hideLikes}
                  onCheckedChange={handleToggleHideLikes}
                  onClick={(e) => e.stopPropagation()}
                />
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={preventClose}
                onClick={handleToggleHideComments}
                className="flex items-center justify-between gap-4"
              >
                <span>Hide comments</span>
                <Switch
                  checked={hideComments}
                  onCheckedChange={handleToggleHideComments}
                  onClick={(e) => e.stopPropagation()}
                />
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={preventClose}
                onClick={handleToggleCommentsClosed}
                className="flex items-center justify-between gap-4"
              >
                <span>Close comments</span>
                <Switch
                  checked={commentsClosed}
                  onCheckedChange={handleToggleCommentsClosed}
                  onClick={(e) => e.stopPropagation()}
                />
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete post</AlertDialogTitle>
            <AlertDialogDescription>
              This post will be permanently deleted. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pinConfirmOpen} onOpenChange={setPinConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace pinned post?</AlertDialogTitle>
            <AlertDialogDescription>
              {replaceTitle ? (
                <>
                  &ldquo;{replaceTitle}&rdquo; is currently pinned{" "}
                  {pin?.scope === "HOME"
                    ? "to the home feed"
                    : "in this channel"}
                  . Only one post can be pinned at a time — pinning this post
                  will unpin it.
                </>
              ) : (
                <>
                  Another post is currently pinned{" "}
                  {pin?.scope === "HOME"
                    ? "to the home feed"
                    : "in this channel"}
                  . Only one post can be pinned at a time — pinning this post
                  will unpin it.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPinning}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmReplacePin}
              disabled={isPinning}
            >
              {isPinning ? "Pinning..." : "Replace pin"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isAdmin && (
        <CreatePostModal
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          channels={channels}
          defaultChannelId={post.channelId ?? undefined}
          editPost={post}
        />
      )}
    </>
  );
}
